var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Recording quality, read from the backend.
 *
 * The screen recording is the platform's largest bandwidth cost by a wide
 * margin: measured at 1,953 kbps encoded / 2,604 kbps on the wire after base64
 * on a real 38.7-minute interview, which is **96% of all upstream traffic**.
 * A later sweep of 80 recent recordings in S3 put the median *actual* bitrate at
 * 2,218 kbps (max 6,874). Nothing constrains it — `getDisplayMedia` is called
 * with no size or frame-rate limit and `MediaRecorder` with no
 * `videoBitsPerSecond`, so the encoder free-runs at whatever the candidate's
 * screen costs. One sampled recording was 3584x2240.
 *
 * These values are admin-tunable rather than hardcoded because the right ones
 * are a judgement about review quality, not an engineering constant, and that
 * judgement should not need a deploy to revise.
 *
 * They are a **CEILING, not a target**. Real footage encodes below the cap (a
 * dense code editor at 8 fps measured ~360 kbps against a 500 kbps cap), and a
 * capability fallback may select a profile *underneath* these numbers — never
 * above. See `recorder.ts`.
 */
import { RECORDING_PROFILES, isProfileId } from '../recording/profiles';
/**
 * What to assume when the config endpoint CANNOT BE REACHED.
 *
 * Deliberately both false even though the server now defaults both true, and
 * this asymmetry is not an oversight — do not "fix" it to match.
 *
 * The multipart control plane IS the backend. If this fetch failed, `init` is
 * going to fail too, so assuming multipart here buys a guaranteed-doomed round
 * trip and then falls back to legacy anyway. Assuming legacy gets to the same
 * place immediately. An unreachable backend is the worst possible moment to
 * start depending on a new one.
 */
export const DEFAULT_RECORDING_FLAGS = {
    multipart_enabled: false,
    config_enforced: false,
};
/**
 * A kill switch, never an enable switch.
 *
 * Set REACT_APP_RECORDING_MULTIPART_ENABLED=false to force the legacy path in a
 * given build regardless of what the server says. It cannot turn multipart ON —
 * that decision belongs to the backend, so a rollout has one control rather
 * than two that can disagree.
 */
export const multipartForcedOff = () => {
    var _a;
    const raw = String((_a = process.env.REACT_APP_RECORDING_MULTIPART_ENABLED) !== null && _a !== void 0 ? _a : '')
        .trim()
        .toLowerCase();
    return raw === '0' || raw === 'false' || raw === 'no' || raw === 'off';
};
/** The effective transport decision. */
export const shouldUseMultipart = (flags) => flags.multipart_enabled && !multipartForcedOff();
/**
 * Used when the endpoint cannot be reached. These MATCH the backend model
 * defaults on purpose: a failed lookup must degrade to the intended settings,
 * not to the unconstrained behaviour this work exists to remove. That is the
 * opposite of how the proctoring capture width behaved when its URL was wrong —
 * it silently fell back and every interview ran at the old value for months
 * (VA-27778). `recording-config.test.ts` pins this equality.
 */
export const DEFAULT_RECORDING_CONFIG = {
    // MEASURED, not chosen by feel: VP9 at 1920x1080/15fps against a 1000 kbps
    // ceiling produced 627 kbps on dense code-editor content — ~141 MB for a
    // 30-minute interview. At the same settings H.264 gave 175 MB and VP8 213 MB,
    // so the codec is what makes full resolution affordable here.
    //
    // `vp9-webm` rather than `auto` on purpose: codec fallback keeps the bitrate
    // ceiling, so a browser that dropped to VP8 at this cap would produce ~213 MB.
    // Naming the profile makes that an exception to notice rather than a routine
    // outcome. VP9 recording is near-universal in current Chrome/Edge/Firefox.
    encoding_profile: 'vp9-webm',
    video_bitrate_bps: 1000000,
    audio_bitrate_bps: 32000,
    max_frame_rate: 15,
    max_width: 1920,
    video_codec: 'vp9',
    container: 'webm',
    audio_enabled: true,
    audio_channels: 1,
    fallback_policy: 'best_effort',
};
const BOUNDS = {
    video_bitrate_bps: [100000, 8000000],
    audio_bitrate_bps: [8000, 256000],
    max_frame_rate: [1, 30],
    max_width: [640, 1920],
    audio_channels: [1, 2],
};
/** Exposed so the admin UI can render bounds without hardcoding its own copy. */
export const RECORDING_BOUNDS = BOUNDS;
const clamp = (key, value) => {
    const [lo, hi] = BOUNDS[key];
    // ABSENT is not the same as ZERO. `Number(null)`, `Number('')` and
    // `Number(false)` are all 0 — finite, and so clamped to the LOWER bound. That
    // would turn a field the backend simply did not send into the smallest legal
    // value (max_width 1280 -> 640), which is silent degradation of exactly the
    // kind this module exists to prevent. A missing field must take the default.
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') {
        return DEFAULT_RECORDING_CONFIG[key];
    }
    const n = Number(value);
    if (!Number.isFinite(n))
        return DEFAULT_RECORDING_CONFIG[key];
    return Math.min(hi, Math.max(lo, Math.round(n)));
};
/**
 * Resolve the profile, accepting configuration written before this field existed.
 *
 * Backward compatibility is the whole job here: a stored document that only has
 * `video_codec: 'vp9'` must keep recording VP9, not silently revert to the VP8
 * default. So an explicit `encoding_profile` wins, and otherwise the legacy
 * codec+container pair is mapped forward.
 */
const asProfileId = (d) => {
    var _a;
    if (isProfileId(d.encoding_profile))
        return d.encoding_profile;
    const container = d.container === 'mp4' ? 'mp4' : 'webm';
    const codec = String((_a = d.video_codec) !== null && _a !== void 0 ? _a : '').toLowerCase();
    if (container === 'mp4' && (codec === 'h264' || codec === 'avc1'))
        return 'h264-mp4';
    if (container === 'webm' && codec === 'vp9')
        return 'vp9-webm';
    if (container === 'webm' && codec === 'vp8')
        return 'vp8-webm';
    return DEFAULT_RECORDING_CONFIG.encoding_profile;
};
/** `video_codec`/`container` are now VIEWS of the profile, never independent. */
const codecOf = (id) => {
    if (id === 'auto')
        return DEFAULT_RECORDING_CONFIG.video_codec;
    return RECORDING_PROFILES[id].codec.toLowerCase().replace('.', '');
};
const containerOf = (id) => id === 'auto' ? 'webm' : RECORDING_PROFILES[id].container;
const asPolicy = (v) => v === 'strict' ? 'strict' : DEFAULT_RECORDING_CONFIG.fallback_policy;
const asBool = (v, fallback) => (typeof v === 'boolean' ? v : fallback);
/** Normalise an arbitrary payload into a safe, in-bounds config. */
export const normalizeRecordingConfig = (data) => {
    const d = (data !== null && data !== void 0 ? data : {});
    const encoding_profile = asProfileId(d);
    return {
        encoding_profile,
        video_bitrate_bps: clamp('video_bitrate_bps', d.video_bitrate_bps),
        audio_bitrate_bps: clamp('audio_bitrate_bps', d.audio_bitrate_bps),
        max_frame_rate: clamp('max_frame_rate', d.max_frame_rate),
        max_width: clamp('max_width', d.max_width),
        audio_channels: clamp('audio_channels', d.audio_channels),
        video_codec: codecOf(encoding_profile),
        container: containerOf(encoding_profile),
        audio_enabled: asBool(d.audio_enabled, DEFAULT_RECORDING_CONFIG.audio_enabled),
        fallback_policy: asPolicy(d.fallback_policy),
    };
};
export const normalizeRecordingFlags = (data) => {
    const d = (data !== null && data !== void 0 ? data : {});
    return {
        multipart_enabled: d.multipart_enabled === true,
        config_enforced: d.config_enforced === true,
    };
};
/** Config AND flags in one read — the endpoint serves both. */
export function fetchRecordingConfigAndFlags(apiBaseUrl, slug) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // The slug lets the server place this interview in a controlled rollout
            // cohort. Omitting it can only ever yield the environment-wide answer,
            // which is the safe reading of a missing parameter.
            const qs = slug ? `?slug=${encodeURIComponent(slug)}` : '';
            const res = yield fetch(`${apiBaseUrl}/recording/config/${qs}`, { method: 'GET' });
            if (!res.ok) {
                return { config: Object.assign({}, DEFAULT_RECORDING_CONFIG), flags: Object.assign({}, DEFAULT_RECORDING_FLAGS) };
            }
            const data = yield res.json();
            return { config: normalizeRecordingConfig(data), flags: normalizeRecordingFlags(data) };
        }
        catch (_a) {
            // A failed lookup degrades to the intended quality settings AND to the
            // legacy transport. Never to multipart: an unreachable control plane is
            // the worst possible moment to start relying on it.
            return { config: Object.assign({}, DEFAULT_RECORDING_CONFIG), flags: Object.assign({}, DEFAULT_RECORDING_FLAGS) };
        }
    });
}
export function fetchRecordingConfig(apiBaseUrl) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            // `apiBaseUrl` already ends in `/api`, and this route is registered
            // alongside `proctoring/config/`. Getting that wrong is exactly how the
            // proctoring capture width was silently ignored for months (VA-27778), so
            // the shape here deliberately matches the one that works.
            const res = yield fetch(`${apiBaseUrl}/recording/config/`, { method: 'GET' });
            if (!res.ok)
                return Object.assign({}, DEFAULT_RECORDING_CONFIG);
            return normalizeRecordingConfig(yield res.json());
        }
        catch (_a) {
            return Object.assign({}, DEFAULT_RECORDING_CONFIG);
        }
    });
}
/** Capture constraints for `getDisplayMedia`, derived from the ceiling.
 *  `max` and not `ideal`: a ceiling the browser may come in under, never over. */
export const recordingCaptureConstraints = (cfg) => ({
    frameRate: { max: cfg.max_frame_rate },
    width: { max: cfg.max_width },
});
/**
 * `MediaRecorder` options at a given tier.
 *
 * `tier` is a 0..1 multiplier on the bitrate ceiling — the hook the capability
 * fallback uses to step quality down without ever touching the admin's
 * configured maximum. 1 means "use the full ceiling".
 */
export const recordingRecorderOptions = (cfg, mimeType, tier = 1) => {
    const scale = Math.min(1, Math.max(0.1, tier));
    const opts = {
        mimeType,
        videoBitsPerSecond: Math.round(cfg.video_bitrate_bps * scale),
    };
    if (cfg.audio_enabled)
        opts.audioBitsPerSecond = cfg.audio_bitrate_bps;
    return opts;
};
/**
 * The bitrate we EXPECT the produced file to land at, for comparison against a
 * measured result.
 *
 * Sum of both encoders plus a small container allowance. A `-c copy` remux of a
 * real browser recording measured ~0.13% muxing overhead, so 1.002 is a fair
 * allowance rather than a guess. Audio is excluded when disabled.
 *
 * This is emphatically NOT a promise about what the encoder will do — see
 * `metrics.ts`.
 */
export const expectedTotalBitrateBps = (cfg, tier = 1) => {
    const scale = Math.min(1, Math.max(0.1, tier));
    const video = cfg.video_bitrate_bps * scale;
    const audio = cfg.audio_enabled ? cfg.audio_bitrate_bps : 0;
    return Math.round((video + audio) * 1.002);
};
